import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Mail, User, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Mensaje enviado correctamente. Te contactaré pronto.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al enviar el mensaje. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contacto | Portfolio"
        description="Ponte en contacto conmigo para discutir tu proyecto. Estoy disponible para colaboraciones y nuevos desafíos."
      />

      <div className="pt-32 pb-24">
        {/* Header */}
        <section className="px-6 md:px-12 lg:px-24 mb-16" data-testid="contact-header">
          <div className="max-w-screen-2xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <p className="text-sm font-sans tracking-widest uppercase text-editorial mb-4">Contacto</p>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8" data-testid="contact-title">
                Trabajemos
                <br />
                juntos
              </h1>
              <p className="text-xl font-light leading-relaxed text-ink/70 max-w-2xl">
                ¿Tienes un proyecto en mente o buscas incorporar talento creativo a tu equipo? Me encantaría 
                escucharlo. Completa el formulario y me pondré en contacto contigo lo antes posible.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="px-6 md:px-12 lg:px-24">
          <div className="max-w-screen-2xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="space-y-12"
                data-testid="contact-info"
              >
                <div>
                  <h2 className="text-3xl font-bold tracking-tight mb-8">Información de Contacto</h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-sm font-sans tracking-widest uppercase text-ink/60 mb-2">Email</h3>
                      <a
                        href="mailto:csolera@gmail.com"
                        className="text-lg hover:text-editorial transition-colors"
                        data-testid="contact-email"
                      >
                        csolera@gmail.com
                      </a>
                    </div>
                    <div>
                      <h3 className="text-sm font-sans tracking-widest uppercase text-ink/60 mb-2">Teléfono</h3>
                      <a
                        href="tel:+34656650273"
                        className="text-lg hover:text-editorial transition-colors"
                        data-testid="contact-phone"
                      >
                        +34 656 65 02 73
                      </a>
                    </div>
                    <div>
                      <h3 className="text-sm font-sans tracking-widest uppercase text-ink/60 mb-2">Ubicación</h3>
                      <p className="text-lg" data-testid="contact-location">
                        Almería, España
                      </p>
                    </div>
                  </div>
                </div>

                {/* Decorative Image */}
                <div className="editorial-border">
                  <img
                    src="https://images.unsplash.com/photo-1616339429079-50ddd3ea0107?crop=entropy&cs=srgb&fm=jpg&q=85"
                    alt="Contact"
                    className="w-full h-96 object-cover grayscale"
                  />
                </div>
              </motion.div>

              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                  <div>
                    <label htmlFor="name" className="block text-sm font-sans tracking-widest uppercase text-ink/60 mb-3">
                      Nombre Completo
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-ink/40" />
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="contact-name-input"
                        className="w-full pl-12 pr-4 py-4 border border-ink/20 focus:border-editorial focus:outline-none transition-colors text-lg font-light"
                        placeholder="Tu nombre"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-sans tracking-widest uppercase text-ink/60 mb-3">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-ink/40" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="contact-email-input"
                        className="w-full pl-12 pr-4 py-4 border border-ink/20 focus:border-editorial focus:outline-none transition-colors text-lg font-light"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-sans tracking-widest uppercase text-ink/60 mb-3">
                      Mensaje
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-4 top-6 w-5 h-5 text-ink/40" />
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="6"
                        data-testid="contact-message-input"
                        className="w-full pl-12 pr-4 py-4 border border-ink/20 focus:border-editorial focus:outline-none transition-colors text-lg font-light resize-none"
                        placeholder="Cuéntame sobre tu proyecto..."
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-button"
                    className="w-full bg-ink text-paper py-5 text-sm font-sans tracking-widest uppercase hover:bg-editorial transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? 'Enviando...' : 'Enviar Mensaje'}
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;